<template>
  <ClientOnly>
    <div class="carousel-container" :style="cssVars">
      <div
        v-if="computedArrows && showPrevArrow"
        class="carousel-arrow-prev"
        @click="handleArrowClick(10)"
      >
        <b-icon icon="chevron-left" />
      </div>
      <div
        v-if="computedArrows && showNextArrow"
        class="carousel-arrow-next"
        @click="handleArrowClick(-10)"
      >
        <b-icon icon="chevron-right" />
      </div>
      <div
        ref="content"
        class="carousel-content"
        :class="{ 'free-mode': freeMode, 'coverflow-centered': coverflowCentered && computedCoverflow }"
      >
        <div ref="items" class="carousel-items" :class="{ 'block-pointer-events': animation }" :style="`gap: ${gapValue}`">
          <div
            v-for="(item, index) in contentItems"
            ref="contentItems"
            :key="item.sort"
            :data-sort="item.sort"
            class="carousel-item"
            :class="[
              {
                active: showActive && isActiveItem(item.sort),
                'is-image': isChildImage(item),
                'coverflow-centered': coverflowCentered && getCoverflowCenteredIndex(index)
              },
            ]"
            :style="`cursor: ${cursor}`"
          >
            <RenderNode
              ref="childContentItem"
              :key="item.sort"
              :value="item.item"
            />
          </div>
        </div>
        <div v-if="$slots['overlay']" ref="overlayItem" class="overlay-item">
          <slot name="overlay" />
        </div>
      </div>
    </div>

    <template #placeholder>
      <template v-if="!getSlotItems().length">
        <div
          v-for="n in 3"
          :key="`placeholder-skeleton-${n}`"
          class="slider-skeleton-item"
        >
          <b-skeleton height="100%" />
        </div>
      </template>
      <template v-else>
        <Slider
          :items="getSlotItems()"
          :gap="gap"
          :visible-items="visibleItems"
          :coverflow="coverflow"
          :coverflow-centered="coverflowCentered"
          :item-ratio="itemRatio"
        >
          <template #default="{ item }">
            <RenderNode :value="item" />
          </template>
          <template v-if="$slots['overlay']" #overlay>
            <slot name="overlay" />
          </template>
        </Slider>
      </template>
    </template>
  </ClientOnly>
</template>

<script>
import RenderNode from '../RenderNode.vue'
import Slider from './Slider.vue'

export default {
  name: 'Carousel',
  components: { RenderNode, Slider },
  props: {
    gap: {
      type: Number,
      default: 0
    },
    visibleItems: {
      type: [Object, Number],
      default: 1
    },
    coverflow: {
      type: [Object, Boolean],
      default: false
    },
    increment: {
      type: Number,
      default: 0
    },
    acceleration: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    showActive: {
      type: Boolean,
      default: false
    },
    arrows: {
      type: [Object, Boolean],
      default: null
    },
    zoom: {
      type: Boolean,
      default: false
    },
    allowKeyboard: {
      type: Boolean,
      default: false
    },
    freeMode: {
      type: Boolean,
      default: false
    },
    allowZoom: {
      type: Boolean,
      default: false
    },
    coverflowCentered: {
      type: Boolean,
      default: false
    },
    initialIndex: {
      type: Number,
      default: 0
    },
    alignItems: {
      type: String,
      default: 'center'
    },
    itemRatio: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      x: 0,
      startX: 0,
      scrollLeft: 0,
      activeItem: 1,
      activeItemIndex: 1,
      contentWidth: 0,
      itemContainerWidth: 0,
      itemsWidth: 0,
      countItems: 0,
      viewportWidth: 0,
      gapValue: this.gap,
      gapCssValue: `${this.gap}px`,
      loopValue: false,
      animation: false,
      incrementValue: 1,
      visibleItemsValue: 1,
      visibleItemsByViewport: null,
      arrowsByViewport: null,
      coverflowByViewport: null,
      firstVisibleItem: 1,
      contentItems: [],
      currentTarget: null,
      panStart: false,
      touchDetails: {
        start: null,
        end: null
      },
      mouseDetails: {
        start: null,
        end: null
      },
      listeners: [],
      grabStart: false,
      capturedLink: null,
      manager: null,
      overlayManager: null,
      zoomed: false,
      itemsCentered: false,
      contentSignature: ''
    }
  },
  computed: {
    cssVars () {
      return {
        '--itemContainerWidthValue': this.itemContainerWidthValue,
        '--gapCssValue': this.gapCssValue,
        '--alignItemsValue': this.alignItems,
        '--overflowValue': this.overflowValue,
        '--scrollBehaviourValue': this.scrollBehaviourValue
      }
    },
    isMobile () {
      return this.viewportWidth <= 767
    },
    isTablet () {
      return this.viewportWidth > 767 && this.viewportWidth <= 1023
    },
    isDesktop () {
      return this.viewportWidth > 1023 && this.viewportWidth < 1408
    },
    isFullhd () {
      return this.viewportWidth >= 1408
    },
    isDesktopUp () {
      return this.isDesktop || this.isFullhd
    },
    getViewportName () {
      let name = ''
      if (this.isMobile) {
        name = 'mobile'
      } else if (this.isTablet) {
        name = 'tablet'
      } else if (this.isDesktop) {
        name = 'desktop'
      } else if (this.isFullhd) {
        name = 'fullhd'
      }
      return name
    },
    computedVisibleItems () {
      return this.visibleItemsByViewport[this.getViewportName]
    },
    computedArrows () {
      return this.contentWidth <= this.itemsWidth && !this.zoomed
        ? this.arrowsByViewport[this.getViewportName]
        : false
    },
    freeModeDesktop () {
      return this.freeMode && this.isDesktopUp
    },
    computedCoverflow () {
      return this.coverflowByViewport[this.getViewportName]
    },
    itemContainerWidthValue () {
      return this.itemContainerWidth ? this.itemContainerWidth + 'px' : '100%'
    },
    overflowValue () {
      return this.acceleration && !this.isDesktopUp ? 'scroll' : 'hidden'
    },
    scrollBehaviourValue () {
      return this.freeModeDesktop ? 'auto' : 'smooth'
    },
    middleItem () {
      return Math.ceil(this.visibleItemsValue / 2)
    },
    showPrevArrow () {
      return this.x < 0
    },
    showNextArrow () {
      return this.x * (-1) + this.contentWidth + (this.computedVisibleItems - 1) * this.gapValue < this.itemsWidth
    },
    cursor () {
      return this.allowZoom && this.zoomed
        ? 'zoom-out'
        : this.allowZoom && !this.zoomed
          ? 'zoom-in'
          : !this.allowZoom && this.showActive
              ? 'pointer'
              : 'auto'
    }
  },
  watch: {
    freeModeDesktop: {
      handler (value) {
        if (this.freeMode) {
          this.$nextTick(() => {
            if (this.manager) {
              this.manager.get('pan').set({ enable: value })
            }
            if (this.$refs.content) {
              this.x = 0
              this.$refs.content.scrollLeft = 0
              this.setTranslate()
            }
          })
        }
      },
      immediate: true
    }
  },
  created () {
    this.visibleItemsByViewport = this.getVisibleItemsByViewport()
    this.arrowsByViewport = this.getArrowsByViewport()
    this.coverflowByViewport = this.getCoverflowByViewport()
    this.applyCoverflowVisibleItems()
    this.activeItem = this.initialIndex + 1
  },
  async mounted () {
    // Placeholder i karuzela kliencka renderują te same VNode'y. Vue 2 cache'uje
    // normalizację scoped slotów po renderze placeholdera, więc przed przejęciem
    // ich przez wersję kliencką trzeba wymusić ponowną normalizację.
    this.resetScopedSlotsCache(this.$slots.default)
    this.resetScopedSlotsCache(this.$slots.overlay)
    // Viewport musi być znany przed init(), bo od niego zależy liczba kopii w loopie
    this.viewportWidth = window.innerWidth
    const isRendered = await this.checkRender()
    if (!isRendered || this._isBeingDestroyed || this._isDestroyed) {
      return
    }
    if (!this.acceleration || (this.acceleration && this.freeMode)) {
      const hasManager = await this.hammer()
      if (!hasManager) {
        return
      }
    }
    if (!this.$refs.content || !this.$refs.items) {
      return
    }
    this.addResizeListener()
    await this.updateVariables()
    if (!this.$refs.content || !this.$refs.items || this._isBeingDestroyed || this._isDestroyed) {
      return
    }
    if (!this.itemsCentered && this.contentWidth >= this.itemsWidth) {
      this.$refs.content.style.display = 'flex'
      this.$refs.content.style.justifyContent = 'center'
      this.itemsCentered = true
      if (this.manager) {
        this.manager.get('pan').set({ enable: false })
      }
    } else if (this.itemsCentered && this.itemsWidth > this.contentWidth) {
      this.$refs.content.style.display = ''
      this.$refs.content.style.justifyContent = ''
      this.itemsCentered = false
    }
    this.addEventListeners()
    this.blockEvents()
    if (!this.freeMode) {
      document.body.style.webkitOverflowScrolling = 'touch'
      if (this.allowKeyboard) {
        window.addEventListener('keydown', this.keydown)
      }
      await this.updateState()
      if (this.loopValue) {
        this.shiftElements()
      }
    }
  },
  async updated () {
    await this.syncContentItems()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('keydown', this.keydown)
    this.removeEventListeners()
    this.releaseCapturedLink()
    if (this.manager) {
      this.manager.destroy()
      this.manager = null
    }
    if (this.overlayManager) {
      this.overlayManager.destroy()
      this.overlayManager = null
    }
  },
  methods: {
    resetScopedSlotsCache (nodes, depth = 0) {
      if (!Array.isArray(nodes) || depth > 12) {
        return
      }
      for (const node of nodes) {
        if (!node) {
          continue
        }
        const scopedSlots = node.data && node.data.scopedSlots
        if (scopedSlots && scopedSlots._normalized) {
          delete scopedSlots._normalized
        }
        this.resetScopedSlotsCache(node.children, depth + 1)
        if (node.componentOptions) {
          this.resetScopedSlotsCache(node.componentOptions.children, depth + 1)
        }
      }
    },
    checkRender (attempt = 0) {
      if (this._isBeingDestroyed || this._isDestroyed) {
        return Promise.resolve(false)
      }

      const content = this.content()
      const hasContent = content.length > 0
      const hasPendingAsyncContent = (this.$slots.default || [])
        .some(x => x && x.asyncMeta && x.asyncMeta.tag === 'CarouselItem')

      // Content musi być gotowy przed przełączeniem ClientOnly z placeholdera,
      // ale inicjalizacja DOM może ruszyć dopiero po utworzeniu ref="content".
      if (hasContent && !hasPendingAsyncContent && !this.contentItems.length) {
        this.init(content)
      }

      if (hasContent && !hasPendingAsyncContent && this.$refs.content) {
        return Promise.resolve(true)
      }

      // Przy pustych slotach (np. dane z backendu jeszcze się ładują)
      // odraczamy inicjalizację, żeby nie liczyć szerokości z countItems = 0.
      if (attempt >= 200) {
        this.init(content)
        return Promise.resolve(false)
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(this.checkRender(attempt + 1))
        }, 16)
      })
    },
    async syncContentItems () {
      const items = this.content()
      const signature = this.getContentSignature(items)
      if (signature === this.contentSignature) {
        return
      }

      this.init(items)
      await this.updateVariables()
      if (!this.freeMode) {
        await this.updateState()
        if (this.loopValue) {
          await this.shiftElements()
        }
      }
    },
    getContentSignature (items) {
      return items
        .map(item => item.item && item.item.key !== undefined ? item.item.key : item.sort)
        .join('|')
    },
    toggleManager (value) {
      if (this.manager) {
        const delay = value ? 0 : 200
        this.zoomed = !value
        setTimeout(() => {
          this.manager.get('pan').set({ enable: value })
        }, delay)
      }
    },
    handleActiveItem (newActiveItem, emit = false) {
      if (this.activeItem !== newActiveItem) {
        this.activeItem = newActiveItem
        this.activeItemIndex =
          this.contentItems.findIndex(el => el.sort === newActiveItem) + 1
        this.updateState(emit)
      }
    },
    addResizeListener () {
      if (this._isBeingDestroyed || this._isDestroyed) {
        return
      }
      if (this.$refs.contentItems && this.$refs.contentItems.length) {
        window.addEventListener('resize', this.resize)
      } else {
        setTimeout(() => {
          this.addResizeListener()
        }, 100)
      }
    },
    addEventListeners () {
      if (this._isBeingDestroyed || this._isDestroyed) {
        return
      }
      if (this.$refs.contentItems) {
        this.$refs.contentItems.forEach((item) => {
          item.addEventListener('mousedown', this.mousedown)
          item.addEventListener('mouseup', this.mouseup)
          item.addEventListener('touchstart', this.touchstart)
          item.addEventListener('touchend', this.touchend, { passive: false })
          this.listeners.push(item)
        })
      } else {
        setTimeout(() => {
          this.addEventListeners()
        }, 100)
      }
    },
    removeEventListeners () {
      this.listeners.forEach((item) => {
        item.removeEventListener('mousedown', this.mousedown)
        item.removeEventListener('mouseup', this.mouseup)
        item.removeEventListener('touchstart', this.touchstart)
        item.removeEventListener('touchend', this.touchend)
      })
    },
    keydown ($event) {
      if ($event.keyCode === 39 && !this.zoomed) {
        // this.zoomOut()
        this.handleArrowClick(-10)
      } else if ($event.keyCode === 37 && !this.zoomed) {
        // this.zoomOut()
        this.handleArrowClick(10)
      } else if ($event.keyCode === 27) {
        this.$emit('close')
      }
    },
    mousedown ($event) {
      if (this.zoomed) {
        return
      }
      this.releaseCapturedLink()
      this.panStart = false
      this.mouseDetails.start = { x: $event.clientX, y: $event.clientY }
    },
    mouseup ($event) {
      if (this.panStart) {
        const closestLink = $event.target.closest('a')
        if (closestLink) {
          this.releaseCapturedLink()
          closestLink.addEventListener('click', this.captureLinkClick, true)
          this.capturedLink = closestLink
        }
      }
      if (this.zoomed) {
        return
      }
      this.mouseDetails.end = { x: $event.clientX, y: $event.clientY }
      if (
        !this.panStart &&
        this.mouseDetails &&
        this.mouseDetails.start &&
        this.mouseDetails.end &&
        Math.abs(this.mouseDetails.end.y - this.mouseDetails.start.y) < 5 &&
        Math.abs(this.mouseDetails.end.x - this.mouseDetails.start.x) < 5
      ) {
        this.currentTarget = $event.currentTarget
        this.handleClick(this.currentTarget.dataset.sort)
      }
    },
    captureLinkClick (e) {
      e.stopPropagation()
      e.preventDefault()
      this.releaseCapturedLink()
    },
    releaseCapturedLink () {
      // Jeśli po przeciągnięciu click nie nastąpił (mouseup poza linkiem),
      // listener zostałby na linku i zablokował kolejne zwykłe kliknięcie.
      if (this.capturedLink) {
        this.capturedLink.removeEventListener('click', this.captureLinkClick, true)
        this.capturedLink = null
      }
    },
    touchstart ($event) {
      if (this.zoomed) {
        return
      }
      this.panStart = false
      this.currentTarget = $event.currentTarget
      if ($event.changedTouches.length === 1) {
        this.touchDetails.start = {
          x: $event.changedTouches[0].clientX,
          y: $event.changedTouches[0].clientY
        }
      }
    },
    touchend ($event) {
      if (this.zoomed) {
        return
      }
      if ($event.cancelable) {
        $event.preventDefault()
      }
      if ($event.changedTouches.length === 1) {
        this.touchDetails.end = {
          x: $event.changedTouches[0].clientX,
          y: $event.changedTouches[0].clientY
        }
      }
      if (
        !this.panStart &&
        Math.abs(this.touchDetails.end.y - this.touchDetails.start.y) < 5 &&
        Math.abs(this.touchDetails.end.x - this.touchDetails.start.x) < 5
      ) {
        this.currentTarget = $event.currentTarget
        const clickable = $event.target.closest('a')
        if (clickable) {
          clickable.click()
        } else {
          this.handleClick(this.currentTarget.dataset.sort)
        }
      }
    },
    disableTouchScroll () {
      window.addEventListener('touchmove', this.preventScroll, { passive: false })
    },
    enableTouchScroll () {
      window.removeEventListener('touchmove', this.preventScroll)
    },
    preventScroll (e) {
      e.preventDefault()
    },
    init (items = null) {
      this.contentItems = items || this.content()
      this.countItems = this.contentItems.length
      this.contentSignature = this.getContentSignature(this.contentItems)
      this.activeItemIndex =
      this.contentItems.findIndex(x => x.sort === this.activeItem) + 1
    },
    applyCoverflowVisibleItems () {
      // Reset ilości widocznych elementów
      if (
        this.coverflow.mobile ||
      this.coverflow.tablet ||
      this.coverflowCentered
      ) {
        this.visibleItemsByViewport.mobile = 1
        this.visibleItemsByViewport.tablet = 1
        if (this.isNumber(this.visibleItems) && this.visibleItems === 1) {
          this.visibleItemsByViewport.desktop = 1
        } else {
          this.visibleItemsByViewport.desktop = 3
        }
      }
    },
    getSlotItems () {
      return (this.$slots.default || [])
        .filter((x) => {
          const tag = x && x.componentOptions && x.componentOptions.tag
          const name = x && x.componentOptions && x.componentOptions.Ctor && x.componentOptions.Ctor.options && x.componentOptions.Ctor.options.name
          const typeName = x && x.type && x.type.name
          return tag === 'CarouselItem' || name === 'CarouselItem' || typeName === 'CarouselItem'
        })
    },
    content () {
      const slotItems = this.getSlotItems()
      const copies = this.getLoopCopies(slotItems.length)
      const items = []
      for (let copy = 0; copy < copies; copy++) {
        slotItems.forEach((item, index) => {
          const sort = copy * slotItems.length + index + 1
          items.push({
            sort,
            key: sort,
            index: sort - 1,
            origin: index + 1,
            item,
            zoom: this.allowZoom
          })
        })
      }
      return items
    },
    getLoopCopies (count) {
      // Loop przestawia elementy w DOM, więc obok środkowego muszą leżeć zapasowe:
      // z coverflow 2 widoczni sąsiedzi + 1 wjeżdżający, bez coverflow 1 + 1.
      // Przy zbyt małej liczbie slajdów renderujemy je kilka razy
      // (Vue sam klonuje VNode użyty drugi raz).
      const visibleItems = this.computedVisibleItems
      if (!this.loop || this.acceleration || count <= visibleItems) {
        return 1
      }
      const minItems = visibleItems + (this.computedCoverflow ? 3 : 2)
      return Math.ceil(minItems / count)
    },
    getOriginSort (sort) {
      const item = this.contentItems.find(x => x.sort === Number(sort))
      return item && item.origin ? item.origin : Number(sort)
    },
    async reset () {
      this.contentItems = this.content()
      this.activeItem = 1
      this.activeItemIndex =
        this.contentItems.findIndex(x => x.sort === this.activeItem) + 1
      if (!this.freeMode) {
        await this.updateState()
      }
    },
    async resize () {
      if (this.viewportWidth && this.viewportWidth !== window.innerWidth && this.$refs.content) {
        await this.updateVariables()
        // Zmiana viewportu może zmienić liczbę kopii slajdów w loopie,
        // więc centrowanie liczymy dopiero na docelowej liczbie slajdów
        await this.syncContentItems()
        if (!this.itemsCentered && this.itemsWidth <= this.contentWidth) {
          this.$refs.content.style.display = 'flex'
          this.$refs.content.style.justifyContent = 'center'
          this.itemsCentered = true
          if (this.manager) {
            this.manager.get('pan').set({ enable: false })
          }
        } else if (this.itemsCentered && this.itemsWidth > this.contentWidth) {
          this.$refs.content.style.display = ''
          this.$refs.content.style.justifyContent = ''
          this.itemsCentered = false
          if (this.manager) {
            this.manager.get('pan').set({ enable: true })
          }
        }
        if (!this.freeMode) {
          await this.updateState()
        }
      }
    },
    blockEvents () {
      if (!this.$refs.items) {
        return
      }
      const events = [
        // 'onclick',
        'onmousedown',
        'onmousemove',
        'onmouseout',
        'onmouseover',
        'onmouseup',
        'ondblclick',
        'onfocus',
        'onblur'
      ]
      events.forEach((event) => {
        this.$refs.items[event] = () => {
          return false
        }
      })
    },
    async hammer () {
      if (process.client) {
        const Hammer = (await import('hammerjs')).default
        const content = this.$refs.content
        if (!content || this._isBeingDestroyed || this._isDestroyed) {
          return false
        }
        const Pan = new Hammer.Pan({
          pointers: 1,
          direction: Hammer.DIRECTION_HORIZONTAL
        })

        const Tap = new Hammer.Tap({
          taps: 1,
          threshold: 2
        })

        this.manager = new Hammer.Manager(content)

        this.manager.add(Pan)

        if (this.$slots.overlay && this.$refs.overlayItem) {
          this.overlayManager = new Hammer.Manager(this.$refs.overlayItem)
          this.overlayManager.add(Tap)
          this.overlayManager.on('tap', (ev) => {
            this.$emit('tap')
          })
        }

        this.manager.on('panstart', (e) => {
          if (this.freeMode) {
            return
          }
          this.panStart = true
        })
        this.manager.on('panmove', (e) => {
          if (this.animation) {
            return
          }
          if (this.freeMode) {
            return
          }
          const x = this.x + e.deltaX / 2
          this.$refs.items.style.transform = `translate(${x}px,0px)`
          this.$refs.items.style.webkitTransform = `translate(${x}px,0px)`
        })
        this.manager.on('panend', (e) => {
          if (this.animation) {
            return
          }
          if (this.freeMode) {
            // const x = this.x + e.deltaX
            // this.x = this.calculateXFreeMode(x)
            // this.setTranslate()
            // this.panStart = false

          } else {
            this.pan(e.deltaX)
          }
        })
        this.manager.on('pancancel', (e) => {
          const lastEvent = e.additionalEvent
          if (!this.freeMode) {
            this.panStart = true
            switch (lastEvent) {
              case 'panright':
                this.pan(10)
                break
              case 'panleft':
                this.pan(-10)
                break
              default:
                this.$refs.items.style.transform = `translate(${this.x}px,0px)`
                this.$refs.items.style.webkitTransform = `translate(${this.x}px,0px)`
                this.panStart = false
            }
          }
        })
        return true
      }
      return false
    },
    async pan (deltaX) {
      if (this.animation) {
        return
      }
      this.x = this.calculateX(this.firstVisibleItem, 'pan', deltaX)
      if (!this.showActive) {
        this.setActiveItem(
          this.contentItems[this.firstVisibleItem - 1].sort,
          true
        )
      }
      this.setActiveItemIndex(this.firstVisibleItem)
      await this.setTranslate()
      if (this.loopValue) {
        await this.shiftElements()
      }
      this.panStart = false
    },
    async setSlide (activeItemIndex, animation = true, emit = false) {
      this.x = this.calculateX(activeItemIndex, 'slide')
      // Indeks ustawiany przed await: równoległe shiftElements (resize + updated)
      // musi widzieć spójny stan, inaczej przestawi slajdy drugi raz
      this.setActiveItemIndex(activeItemIndex)
      await this.setTranslate(animation)
      // this.setActiveItem(this.contentItems[activeItemIndex - 1].sort, emit)
    },
    async setSlideFreeMode (n) {
      this.x = this.calculateXFreeMode(-1 * n * this.itemContainerWidth)
      await this.setTranslate()
    },
    setTranslate (animation = true) {
      return new Promise((resolve, reject) => {
        if (animation) {
          this.animation = true
          this.disableTouchScroll()
          if (this.$refs.items && this.$refs.items.style) {
            this.$refs.items.style.transition = 'transform .25s ease-in-out'
          }
        }
        if (this.$refs.items && this.$refs.items.style) {
          this.$refs.items.style.transform = `translate(${this.x}px,0px)`
          this.$refs.items.style.webkitTransform = `translate(${this.x}px,0px)`
        }

        if (animation) {
          setTimeout(() => {
            this.animation = false
            this.enableTouchScroll()
            if (this.$refs.items && this.$refs.items.style) {
              this.$refs.items.style.transition = ''
            }
            resolve()
          }, 260)
        } else {
          resolve()
        }
      })
    },
    calculateX (n, type, deltaX) {
      let tx = this.x
      let newFirstVisibleItem = this.firstVisibleItem
      // PAN
      // Liczenie nowego X dla przesunięcia (pan)
      // czyli ręcznego przesunięcia listy elementów
      // SLIDE
      // Ustawianie pierwszego widocznego elementu na podstawie numeru slajdu
      // Obliczenie przesunięcia dla karuzelki
      if (type === 'pan') {
        // Jeśli itemów jest mniej niż ma zostać wyświetlone
        // to kafelki nie są przesuwalne tylko wyświetlane od lewej strony
        if (this.countItems <= this.visibleItemsValue) {
          newFirstVisibleItem = 1
        } else {
          if (deltaX < 0) {
            if (
              this.firstVisibleItem + this.incrementValue <=
              this.countItems - this.visibleItemsValue + 1
            ) {
              newFirstVisibleItem = this.firstVisibleItem + this.incrementValue
            } else {
              newFirstVisibleItem = this.countItems - this.visibleItemsValue + 1
            }
          } else if (deltaX > 0) {
            if (this.firstVisibleItem - this.incrementValue >= 1) {
              newFirstVisibleItem = this.firstVisibleItem - this.incrementValue
            } else {
              newFirstVisibleItem = 1
            }
          }
          // Wyliczenie nowego X
          if (this.computedCoverflow) {
            const initialTx = this.itemContainerWidth * 0.1
            tx =
              -1 * ((newFirstVisibleItem - 1) * this.itemContainerWidth) -
                (newFirstVisibleItem - 2) * this.gapValue +
                initialTx
          } else {
            tx =
              -1 *
                (newFirstVisibleItem - 1) *
                (this.itemContainerWidth + this.gapValue)
          }
        }
      } else if (type === 'slide') {
        const initialTx = this.itemContainerWidth * 0.1
        // Jeśli itemów jest mniej niż ma zostać wyświetlone
        // to kafelki są wyświetlane od lewej strony
        if (this.countItems <= this.visibleItemsValue) {
          newFirstVisibleItem = 1
          tx =
            -1 *
              (newFirstVisibleItem - 1) *
              (this.itemContainerWidth + this.gapValue)
        } else if (this.computedCoverflow && this.loopValue) {
          newFirstVisibleItem = n
          tx =
            -1 * ((n - 1) * this.itemContainerWidth) -
              (n - 2) * this.gapValue +
              initialTx
        } else if (!this.computedCoverflow && this.loopValue) {
          newFirstVisibleItem = n
          tx =
            -1 *
              (newFirstVisibleItem - 1) *
              (this.itemContainerWidth + this.gapValue)
        } else {
          if (n <= this.middleItem) {
            newFirstVisibleItem = 1
          } else if (
            n > this.middleItem &&
            n < this.countItems - this.middleItem + 1
          ) {
            newFirstVisibleItem = n - this.middleItem + 1
          } else if (n >= this.countItems - this.middleItem + 1) {
            newFirstVisibleItem = this.countItems - this.visibleItemsValue + 1
          }
          if (this.computedCoverflow) {
            tx =
              -1 * ((newFirstVisibleItem - 1) * this.itemContainerWidth) -
                (newFirstVisibleItem - 2) * this.gapValue +
                initialTx
          } else {
            tx =
              -1 *
                (newFirstVisibleItem - 1) *
                (this.itemContainerWidth + this.gapValue)
          }
        }
      }
      this.firstVisibleItem = newFirstVisibleItem
      return tx
    },
    calculateXFreeMode (x) {
      if (x > 0) {
        x = 0
      } else if (
        this.itemsWidth > this.contentWidth &&
        x < -1 * (this.itemsWidth - this.contentWidth)
      ) {
        x = -1 * (this.itemsWidth - this.contentWidth)
      }
      return x
    },
    setScrollLeft (x) {
      this.$nextTick(() => {
        this.$refs.content.scrollTo(-1 * x, 0)
      })
    },
    setActiveItem (n, emit = false) {
      if (emit) {
        this.$emit('update', this.getOriginSort(n))
      }
      this.activeItem = Number(n)
    },
    setActiveItemIndex (n) {
      this.activeItemIndex = Number(n)
    },
    async shiftElements () {
      // Wszystkie elementy mieszczą się na ekranie - nie ma czego zapętlać,
      // ale pozycja mogła zostać z innego viewportu (np. z mobile z kopiami)
      if (this.countItems <= this.visibleItemsValue) {
        await this.setSlide(1, false)
        return
      }
      if (!this.hasPrevItem()) {
        const coverflowCorrection =
          this.computedCoverflow && this.activeItemIndex < 2 ? 1 : 0
        for (let i = coverflowCorrection; i >= 0; i--) {
          this.contentItems[this.contentItems.length - 1 - i].index = -1 - i
        }
        this.contentItems.sort((a, b) => a.index - b.index)
        this.contentItems.forEach((item, index) => {
          item.index = index
        })
        const newActiveItemIndex = this.computedCoverflow ? 3 : 2
        await this.setSlide(newActiveItemIndex, false)
      } else if (!this.hasNextItem()) {
        const coverflowCorrection = this.computedCoverflow
          ? this.visibleItemsValue
          : 0
        this.contentItems[0].index = this.contentItems.length
        this.contentItems.sort((a, b) => a.index - b.index)
        this.contentItems.forEach((item, index) => {
          item.index = index
        })
        await this.setSlide(
          this.contentItems.length -
          1 -
          (this.visibleItemsValue - 1) -
          coverflowCorrection,
          false
        )
      } else {
        await this.setSlide(this.activeItemIndex, this.showActive, false)
      }
    },
    async updateState (emit = false) {
      if (this.acceleration) {
        this.setActiveItem(this.contentItems[this.activeItemIndex - 1].sort)
        this.setScrollLeft(this.calculateX(this.activeItemIndex, 'slide'))
      } else if (this.loopValue) {
        await this.shiftElements()
      } else {
        await this.setSlide(this.activeItemIndex, this.showActive, emit)
      }
    },
    getCoverflowCenteredIndex (index) {
      return this.isMobile || this.isTablet
        ? index === this.activeItemIndex - 1
        : this.isDesktopUp && this.countItems >= 3
          ? index === this.activeItemIndex
          : true
    },
    getVisibleItemsByViewport () {
      let [mobile, tablet, desktop, fullhd] = [1, 1, 1, 1]
      if (this.isNumber(this.visibleItems)) {
        // Jeśli przekazana sama liczba -> ustaw na każdy viewport to samo
        mobile = this.visibleItems
        tablet = this.visibleItems
        desktop = this.visibleItems
        fullhd = this.visibleItems
      } else if (this.isObject(this.visibleItems)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub 1
        mobile = this.isNumber(this.visibleItems.mobile)
          ? this.visibleItems.mobile
          : 1
        tablet = this.isNumber(this.visibleItems.tablet)
          ? this.visibleItems.tablet
          : mobile
        desktop = this.isNumber(this.visibleItems.desktop)
          ? this.visibleItems.desktop
          : tablet
        fullhd = this.isNumber(this.visibleItems.fullhd)
          ? this.visibleItems.fullhd
          : desktop
      }
      return { mobile, tablet, desktop, fullhd }
    },
    getArrowsByViewport () {
      let [mobile, tablet, desktop, fullhd] = [false, false, false, false]
      if (this.isBoolean(this.arrows)) {
        // Jeśli przekazane true/false -> ustaw na każdy viewport to samo
        mobile = this.arrows
        tablet = this.arrows
        desktop = this.arrows
        fullhd = this.arrows
      } else if (this.isObject(this.arrows)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub false
        mobile = this.isBoolean(this.arrows.mobile) ? this.arrows.mobile : false
        tablet = this.isBoolean(this.arrows.tablet)
          ? this.arrows.tablet
          : mobile
        desktop = this.isBoolean(this.arrows.desktop)
          ? this.arrows.desktop
          : tablet
        fullhd = this.isBoolean(this.arrows.fullhd)
          ? this.arrows.fullhd
          : desktop
      }
      return { mobile, tablet, desktop, fullhd }
    },
    getCoverflowByViewport () {
      let [mobile, tablet, desktop, fullhd] = [false, false, false, false]
      if (this.isBoolean(this.coverflow)) {
        // Jeśli przekazane true/false -> ustaw na każdy viewport to samo
        mobile = this.coverflow
        tablet = this.coverflow
        desktop = this.coverflow
        fullhd = this.coverflow
      } else if (this.isObject(this.coverflow)) {
        // Jeśli przekazany obiekt -> ustaw każdy z osobna lub poprzedni lub false
        mobile = this.isBoolean(this.coverflow.mobile)
          ? this.coverflow.mobile
          : false
        tablet = this.isBoolean(this.coverflow.tablet)
          ? this.coverflow.tablet
          : mobile
        // Warunek do dopisania jeśli będzie potrzeba tego na desktopie
        // desktop = this.isBoolean(this.coverflow.desktop)
        //   ? this.coverflow.desktop
        //   : tablet
        desktop = false
        fullhd = false
      }
      return { mobile, tablet, desktop, fullhd }
    },
    handleClick (item) {
      if (this.showActive) {
        this.setActiveItem(item, true)
      } else if (this.zoom) {
        this.emitZoom()
      }
    },
    handleArrowClick (deltaX) {
      if (this.freeMode) {
        // TODO: napraw obsluge strzalek (glownie Werbe)
        // let n = 0
        // if (deltaX > 0) {
        //   n = this.getPrevActiveItemFreeMode()
        // } else if (deltaX < 0) {
        //   n = this.getNextActiveItemFreeMode()
        // }
        // this.setSlideFreeMode(n)

      } else {
        this.panStart = true
        this.pan(deltaX)
      }
    },
    emitZoom () {
      this.$emit('openModal', this.getOriginSort(this.activeItem))
    },
    async updateVariables () {
      this.viewportWidth = window.innerWidth
      this.countItems = this.contentItems.length
      this.gapValue = this.gap
      this.contentWidth = this.$refs.content
        ? this.$refs.content.clientWidth
        : 0
      this.gapCssValue = `${this.gapValue}px`
      this.loopValue = this.acceleration ? false : this.loop

      if (!this.countItems) {
        this.itemContainerWidth = 0
        this.itemsWidth = 0
        return
      }

      if (this.freeMode) {
        await this.$nextTick(() => {
          if (this.$refs.contentItems && this.$refs.contentItems[0]) {
            const childElement =
              this.$refs.contentItems[0]?.children[0]?.children[0]?.children[0]
            if (childElement) {
              let width = window
                .getComputedStyle(childElement)
                .getPropertyValue('width')
              width = Number(width.slice(0, -2))
              if (this.isNumber(width)) {
                this.itemContainerWidth = width
                this.itemsWidth =
                  this.countItems * this.itemContainerWidth +
                    (this.countItems - 1) * this.gapValue
              }
            }
          }
        })
        this.contentWidth = this.$refs.content
          ? this.$refs.content.clientWidth
          : 0
      } else {
        await this.$nextTick(() => {
          this.contentWidth = this.$refs.content
            ? this.$refs.content.clientWidth
            : 0
          this.visibleItemsValue = this.computedVisibleItems
          if (this.computedCoverflow && !this.coverflowCentered) {
            const x = (this.contentWidth - 2 * this.gapValue) / 1.2
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          } else if (this.computedCoverflow && this.coverflowCentered) {
            // this.gapValue = 0
            const x = (this.contentWidth - 2 * this.gapValue) / 1.2
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          } else {
            const x =
            (this.contentWidth - (this.visibleItemsValue - 1) * this.gapValue) /
              this.visibleItemsValue
            const itemsWidth =
            this.countItems * x + (this.countItems - 1) * this.gapValue
            this.itemContainerWidth = x
            this.itemsWidth = itemsWidth
          }
          // Ustaw increment, jeśli nie przekazany = ilosc widocznych elementów
          this.incrementValue = this.increment
            ? this.increment
            : this.computedCoverflow
              ? 1
              : this.visibleItemsValue
        })
      }
    },
    // setStyles () {
    //   // Ustalenie wielkosci wyswietlonych elementów
    //   if (this.freeMode) {
    //     this.$nextTick(() => {
    //       const firstElement = this.$refs.contentItems[0]
    //       this.itemContainerWidth = firstElement.offsetWidth
    //       this.itemsWidth =
    //         this.countItems * this.itemContainerWidth +
    //         (this.countItems - 1) * this.gap
    //     })
    //   } else {
    //     this.itemContainerWidth =
    //       (this.contentWidth - (this.visibleItemsValue - 1) * this.gap) /
    //       this.visibleItemsValue
    //     if (this.computedCoverLeftRight) {
    //       this.itemContainerWidth =
    //         (this.contentWidth - (this.visibleItemsValue + 1) * this.gap) /
    //         (this.visibleItemsValue + 0.1)
    //     }
    //   }
    // },
    getActiveItem () {
      return this.activeItem
    },
    getPrevActiveItemFreeMode () {
      return Math.ceil(Math.abs(this.x) / this.itemContainerWidth) - 1
    },
    getNextActiveItemFreeMode () {
      return Math.floor(Math.abs(this.x) / this.itemContainerWidth) + 1
    },
    isActiveItem (index) {
      return this.visibleItemsValue > 1 && this.activeItem === index
    },
    isChildImage (child) {
      return (
        child &&
        child.item &&
        child.item.type &&
        child.item.type.name === 'CarouselItem'
      )
    },
    hasPrevItem () {
      const coverflowCorrection = this.computedCoverflow ? 1 : 0
      return this.activeItemIndex - coverflowCorrection > 1
    },
    hasNextItem () {
      const coverflowCorrection = this.computedCoverflow
        ? this.visibleItemsValue
        : 0
      return (
        this.activeItemIndex + coverflowCorrection + this.visibleItemsValue - 1 < this.countItems
      )
    },
    isObject (item) {
      return typeof item === 'object' && !Array.isArray(item) && item !== null
    },
    isNumber (item) {
      return typeof item === 'number'
    },
    isBoolean (item) {
      return typeof item === 'boolean'
    }
  }
}
</script>

<style lang="scss" scoped>
.carousel-container {
  position: relative;
  width: 100%;
  // max-height: fit-content;
  // @include mobile {
  //   width: calc(100% + 50px);
  //   margin: 0 -25px;
  // }
  .carousel-arrow-prev,
  .carousel-arrow-next {
    position: absolute;
    top: calc(50% - 20px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(#eee, 0.5);
    z-index: 10;
    cursor: pointer;

    &:hover {
      background-color: rgba(#eee, 0.8);
    }

    svg {
      display: block;
      width: 20px;
      height: 20px;
    }
  }

  .carousel-arrow-prev {
    left: -5%;
      @media screen and (max-width: 1485px){
      left: -20px;
    }
      @include mobile{
    left: 0;
  }

    svg {
      margin-left: -3px;
    }
  }

  .carousel-arrow-next {
    right: -5%;
    @media screen and (max-width: 1485px){
      right: -20px;
    }
     @include mobile{
    right: 0;
    }

    svg {
      margin-right: -3px;
    }
  }

  .carousel-content {
    width: 100%;
    height: fit-content;
    position: relative;
    margin: 0 auto;
    max-width: 100%;
    overflow: hidden;
    overflow-x: var(--overflowValue);
    scroll-behavior: var(--scrollBehaviourValue);
    touch-action: pan-y;
    // @include touch{
    //   margin-left: -24px;
    //   margin-right: -24px;
    // }

    &.free-mode {
      touch-action: auto !important;
    }

    &.coverflow-centered {
      .carousel-item {
        scale: 0.9;
        transition: scale 0.3s ease-out;

        &.coverflow-centered {
          scale: 1;
        }
      }
    }

    &.smooth-scroll {
      scroll-behavior: smooth !important;
    }

    .carousel-items {
      width: auto;
      display: inline-flex;
      align-items: var(--alignItemsValue) !important;
      scroll-behavior: smooth;
      touch-action: auto !important;
      gap: var(--gapCssValue) !important;

      & > * {
        width: var(--itemContainerWidthValue);
        flex-shrink: 0;

        & > * {
          width: 100% !important;
        }
      }

      .carousel-item {
        cursor: pointer;
        align-self: start;

        &.active {
          opacity: 0.3;
        }
      }
    }

    & .overlay-item {
      height: fit-content;
      position: absolute;
      bottom: calc(3.5% + 7px);
      left: 50%;
      transform: translateX(-50%);
      font-size: 14px;
      }
   }

  .block-pointer-events {
    pointer-events: none;
  }
}

// .carousel-container .carousel-content .carousel-items > .carousel-item {
//   aspect-ratio:1/1;
// }

.slider-skeleton-item {
  width: 100%;
  aspect-ratio: 1/2;
  min-width: 0;
  flex-shrink: 0;
}

.slider-skeleton-item > .b-skeleton,
.slider-skeleton-item .b-skeleton-item {
  height: 100%;
  margin: 0;
}
</style>
